import React, { useState } from "react";
import { Badge, Button, Card, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography } from "antd";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useCreate, useDelete, useLoadList, useTableSelection, useUpdate } from "../../../hooks";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { formatDateTime } from "../../../utils/dateUtils";
import { measurementUnitApi } from "../../../api/v3/masterDataApi";

const { Text } = Typography;
const { confirm } = Modal;

const UNIT_CATEGORY_OPTIONS = [
    { value: "COUNT", label: "数量" },
    { value: "MASS", label: "重量" },
    { value: "VOLUME", label: "体积" },
    { value: "TIME", label: "时间" },
    { value: "DISTANCE", label: "距离" },
    { value: "GENERAL", label: "通用" },
];

const UNIT_CATEGORY_LABELS = Object.fromEntries(UNIT_CATEGORY_OPTIONS.map((item) => [item.value, item.label]));

const MeasurementUnitList = () => {
    const [filterForm] = Form.useForm();
    const [form] = Form.useForm();
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const [formOpen, setFormOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const { data, isLoading, refetch } = useLoadList("计量单位", measurementUnitApi.listUnits, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    const { tableRef, getIndexColumn } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
    });

    const createMutation = useCreate("计量单位", measurementUnitApi.createUnit, {
        onSuccess: () => {
            setFormOpen(false);
            form.resetFields();
            refetch();
        },
    });

    const updateMutation = useUpdate("计量单位", measurementUnitApi.updateUnit, {
        onSuccess: () => {
            setFormOpen(false);
            setEditingRecord(null);
            form.resetFields();
            refetch();
        },
    });

    const deleteMutation = useDelete("计量单位", measurementUnitApi.deleteUnit, {
        onSuccess: () => refetch(),
    });

    const handleSearch = () => {
        const values = filterForm.getFieldsValue();
        setFilterParams({
            search: values.search || undefined,
            category: values.category,
            is_active: values.is_active,
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleReset = () => {
        filterForm.resetFields();
        setFilterParams({});
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const openCreate = () => {
        setEditingRecord(null);
        form.resetFields();
        form.setFieldsValue({ category: "COUNT", is_active: true, sort_order: 0 });
        setFormOpen(true);
    };

    const openEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            code: record.code,
            display_name: record.display_name,
            category: record.category,
            description: record.description,
            is_active: record.is_active,
            sort_order: record.sort_order,
        });
        setFormOpen(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (editingRecord) {
                updateMutation.mutate({ id: editingRecord.id, data: values });
                return;
            }
            createMutation.mutate(values);
        } catch (error) {
            if (!error?.errorFields) {
                throw error;
            }
        }
    };

    const handleDelete = (record) => {
        confirm({
            title: "确认删除单位",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除单位 "${record.display_name}" 吗？如果已被 AP 产品知识库使用，后端会拒绝删除。`,
            okText: "删除",
            okButtonProps: { danger: true },
            cancelText: "取消",
            onOk: () => deleteMutation.mutate(record.id),
        });
    };

    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "单位代码",
            dataIndex: "code",
            key: "code",
            width: 140,
            render: (text) => <Text code>{text}</Text>,
        },
        {
            title: "展示名称",
            dataIndex: "display_name",
            key: "display_name",
            width: 150,
        },
        {
            title: "类别",
            dataIndex: "category",
            key: "category",
            width: 100,
            render: (category) => <Tag>{UNIT_CATEGORY_LABELS[category] || category}</Tag>,
        },
        {
            title: "说明",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            render: (text) => text || <Text type="secondary">-</Text>,
        },
        {
            title: "排序",
            dataIndex: "sort_order",
            key: "sort_order",
            width: 90,
            align: "center",
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (active) => <Badge status={active ? "success" : "default"} text={active ? "启用" : "停用"} />,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 170,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 130,
            render: (_, record) => (
                <Space size="small">
                    <PermissionGuard permission="masterdata:update">
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="masterdata:delete">
                        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
                            删除
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title">
                    <h2>计量单位</h2>
                    <span className="page-description">配置产品、结算、运营等业务可复用的通用单位</span>
                </div>
                <div className="page-actions">
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
                            刷新
                        </Button>
                        <PermissionGuard permission="masterdata:create">
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                                新增单位
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            <Card className="filter-card" size="small">
                <Form form={filterForm} layout="inline" className="filter-form">
                    <Form.Item name="search" label="关键词">
                        <Input placeholder="代码 / 名称 / 说明" allowClear onPressEnter={handleSearch} />
                    </Form.Item>
                    <Form.Item name="category" label="类别">
                        <Select placeholder="全部" allowClear style={{ width: 120 }} options={UNIT_CATEGORY_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="is_active" label="状态">
                        <Select
                            placeholder="全部"
                            allowClear
                            style={{ width: 120 }}
                            options={[
                                { value: true, label: "启用" },
                                { value: false, label: "停用" },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                                搜索
                            </Button>
                            <Button onClick={handleReset}>重置</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card className="data-card">
                <Table
                    ref={tableRef}
                    columns={columns}
                    dataSource={data?.items || []}
                    loading={isLoading}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条`,
                    }}
                    onChange={(nextPagination) => setPagination({ current: nextPagination.current, pageSize: nextPagination.pageSize })}
                    scroll={{ x: 1080 }}
                />
            </Card>

            <Modal
                title={editingRecord ? "编辑单位" : "新增单位"}
                open={formOpen}
                onOk={handleSubmit}
                onCancel={() => {
                    setFormOpen(false);
                    setEditingRecord(null);
                    form.resetFields();
                }}
                confirmLoading={createMutation.isLoading || updateMutation.isLoading}
                destroyOnClose
                width={560}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="code" label="单位代码" rules={[{ required: true, message: "请输入单位代码" }]}>
                        <Input placeholder="如 个、kg、USG" maxLength={50} />
                    </Form.Item>
                    <Form.Item name="display_name" label="展示名称" rules={[{ required: true, message: "请输入展示名称" }]}>
                        <Input placeholder="如 个、千克、美制加仑" maxLength={100} />
                    </Form.Item>
                    <Form.Item name="category" label="类别" rules={[{ required: true, message: "请选择类别" }]}>
                        <Select options={UNIT_CATEGORY_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="description" label="说明">
                        <Input.TextArea rows={3} placeholder="说明该单位的业务含义" />
                    </Form.Item>
                    <Form.Item name="sort_order" label="排序">
                        <InputNumber min={0} precision={0} style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name="is_active" label="状态" valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="停用" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MeasurementUnitList;
